import {Checkbox} from "antd"
import {AddItemCircleButton, BaseInput} from "components"
import CampusSelect from "components/CampusSelect"
import {BaseTimePicker} from "components/DateTimePicker"
import ShiftSelect from "components/ShiftSelect"
import {scrollTableToBottom} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useAllCampuses, useAllShifts, useModel} from "hooks"
import {cloneDeep} from "lodash"
import moment from "moment"
import {useCallback, useEffect, useMemo} from "react"
import {useTranslation} from "react-i18next"
import {KlassappTableProps} from "types/common"
import {DailySpecialAttendanceRoundingRuleRow} from "types/settings/general-setting"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import styles from "./DailySpecialRoundingRulesTable.module.css"

type Props = {
    listData: DailySpecialAttendanceRoundingRuleRow[]
    setListData: (payload: DailySpecialAttendanceRoundingRuleRow[]) => void
} & KlassappTableProps

const TIME_STORAGE_FORMAT = "HH:mm"

const DailySpecialRoundingRulesTable = (props: Props) => {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        isLoading,
        onChangePage,
        onChangeRowPerPage,
        isShowTableHeaderAction,
        onUpdateRowData,
        getCurrentData,
        onUpdateTableData,
        onChangeFields,
        onChangeAllFields,
        listData,
        setListData
    } = props
    const {t} = useTranslation(["common"])
    const model = useModel()
    const userTimeFormat = model.getUserTimeFormat()
    const {campuses: allCampuses} = useAllCampuses()
    const {shifts: allShifts} = useAllShifts()

    const campusById = useMemo(() => {
        const map = new Map<number, {id: number; name?: string}>()
        ;(allCampuses || []).forEach((campus: any) => {
            map.set(campus.id, campus)
        })
        return map
    }, [allCampuses])

    const shiftById = useMemo(() => {
        const map = new Map<number, {shiftId: number; name?: string}>()
        ;(allShifts || []).forEach((shift: any) => {
            map.set(shift.shiftId, {shiftId: shift.shiftId, name: shift.name})
        })
        return map
    }, [allShifts])

    const resolveRow = useCallback(
        (row: DailySpecialAttendanceRoundingRuleRow): DailySpecialAttendanceRoundingRuleRow => {
            return {
                ...row,
                campuses: (row.campuses || []).map((campus) => campusById.get(campus.id) || campus),
                shifts: (row.shifts || []).map((shift) => shiftById.get(shift.shiftId) || shift)
            }
        },
        [campusById, shiftById]
    )

    const onChangeNewItemData = (payload: {key: string; value: any; index: number}) => {
        const {key, value, index} = payload
        const newListData = cloneDeep(listData)
        newListData[index] = {...newListData[index], [key]: value}

        if (key === "campuses") {
            const campusIds = (value || []).map((campus: any) => campus.id)
            newListData[index].shifts = (newListData[index].shifts || []).filter((shift) => {
                const shiftCampusIds =
                    (allShifts || [])
                        .find((item: any) => item.shiftId === shift.shiftId)
                        ?.campuses?.map((c: any) => c.id) || []
                if (!shiftCampusIds.length) return true
                return shiftCampusIds.some((id: number) => campusIds.includes(id))
            })
        }

        setListData(newListData)
    }

    const parseTimeValue = (value?: string | null) => {
        if (!value) return null
        const parsed = moment(value, [TIME_STORAGE_FORMAT, userTimeFormat, moment.ISO_8601], true)
        return parsed.isValid() ? parsed : null
    }

    const onClickDeleteMulti = useCallback(async () => {
        const checkedItems = getCurrentData().filter((item: any) => !item.isChecked)
        dispatch({isShowTableHeaderAction: false, isHideMenuActions: false})
        setListData(cloneDeep(checkedItems))
    }, [dispatch, getCurrentData, setListData])

    const formattedListData = data.map((item, index) => {
        const row = resolveRow(item)
        const campusIds = (row.campuses || []).map((campus) => campus.id)

        return {
            ...row,
            campusHtml: (
                <div className={styles.cellControl}>
                    <CampusSelect
                        isMulti
                        isClearable
                        placeholder="Campus"
                        value={row.campuses || []}
                        onChange={(newValue) => onChangeNewItemData({key: "campuses", value: newValue || [], index})}
                    />
                </div>
            ),
            shiftHtml: (
                <div className={styles.cellControl}>
                    <ShiftSelect
                        isMulti
                        isClearable
                        campusIds={campusIds}
                        placeholder="Shift"
                        value={row.shifts || []}
                        onChange={(newValue) => onChangeNewItemData({key: "shifts", value: newValue || [], index})}
                    />
                </div>
            ),
            fromHtml: (
                <div className={styles.cellTime}>
                    <BaseTimePicker
                        allowClear={false}
                        format={userTimeFormat}
                        value={parseTimeValue(row.from)}
                        onChange={(newValue) =>
                            onChangeNewItemData({
                                key: "from",
                                value: newValue ? moment(newValue).format(TIME_STORAGE_FORMAT) : undefined,
                                index
                            })
                        }
                    />
                </div>
            ),
            toHtml: (
                <div className={styles.cellTime}>
                    <BaseTimePicker
                        allowClear={false}
                        format={userTimeFormat}
                        value={parseTimeValue(row.to)}
                        onChange={(newValue) =>
                            onChangeNewItemData({
                                key: "to",
                                value: newValue ? moment(newValue).format(TIME_STORAGE_FORMAT) : undefined,
                                index
                            })
                        }
                    />
                </div>
            ),
            roundingValueHtml: (
                <div className={styles.cellTime}>
                    <BaseTimePicker
                        allowClear={false}
                        format={userTimeFormat}
                        value={parseTimeValue(row.roundingValue)}
                        onChange={(newValue) =>
                            onChangeNewItemData({
                                key: "roundingValue",
                                value: newValue ? moment(newValue).format(TIME_STORAGE_FORMAT) : undefined,
                                index
                            })
                        }
                    />
                </div>
            ),
            noAttendanceHtml: (
                <div className={styles.cellCheckbox}>
                    <Checkbox
                        checked={!!row.noAttendance}
                        onChange={(event) =>
                            onChangeNewItemData({key: "noAttendance", value: event.target.checked, index})
                        }
                    />
                </div>
            ),
            afterHtml: (
                <div className={styles.cellTime}>
                    <BaseTimePicker
                        allowClear
                        format={userTimeFormat}
                        value={parseTimeValue(row.after)}
                        onChange={(newValue) =>
                            onChangeNewItemData({
                                key: "after",
                                value: newValue ? moment(newValue).format(TIME_STORAGE_FORMAT) : null,
                                index
                            })
                        }
                    />
                </div>
            ),
            messageHtml: (
                <div className={styles.cellMessage}>
                    <BaseInput
                        placeholder="Message"
                        value={row.message || ""}
                        onChange={(value) => onChangeNewItemData({key: "message", value, index})}
                    />
                </div>
            )
        }
    })

    useEffect(() => {
        dispatch({data: listData, total: listData.length})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listData])

    const getFields = () => {
        return ["Campus", "Shift", "From", "To", "Rounding Value", "No Attendance", "After", "Message"]
    }

    const getPageTitle = () => {
        return "Special Rounding Rules"
    }

    const getColumns = () => {
        return [
            {title: "Campus", field: "campusHtml", headerStyle: {minWidth: "180px"}},
            {title: "Shift", field: "shiftHtml", headerStyle: {minWidth: "160px"}},
            {title: "From", field: "fromHtml", headerStyle: {minWidth: "110px"}},
            {title: "To", field: "toHtml", headerStyle: {minWidth: "110px"}},
            {title: "Rounding Value", field: "roundingValueHtml", headerStyle: {minWidth: "120px"}},
            {title: "No Attendance", field: "noAttendanceHtml", headerStyle: {minWidth: "100px"}},
            {title: "After", field: "afterHtml", headerStyle: {minWidth: "110px"}},
            {title: "Message", field: "messageHtml", headerStyle: {minWidth: "160px"}}
        ]
    }

    const getTableHeaderActions = () => {
        const {onClickShowConfirmModal} = props
        return [
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            }
        ]
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFields", func: getFields},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onClickDeleteMulti])

    const onClickAddItem = useCallback(() => {
        setListData([
            ...listData,
            {
                id: `new-${listData.length}-${Date.now()}`,
                campuses: [],
                shifts: [],
                from: undefined,
                to: undefined,
                roundingValue: undefined,
                noAttendance: false,
                after: null,
                message: ""
            }
        ])
        scrollTableToBottom(props.tableRef)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listData, setListData])

    return (
        <div>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                isShowAllRecords
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
            />
            <KlassappTable
                columns={columns}
                data={formattedListData}
                menuActions={[]}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn
                tableRef={props.tableRef}
                onClickRowItem={() => {}}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <AddItemCircleButton onClick={onClickAddItem} />
        </div>
    )
}

export default KlassappTableHOC(DailySpecialRoundingRulesTable)
